import produce from 'immer'
import {
  append,
  isNil,
  remove 
} from 'ramda'
import React,
{
  Component,
  Fragment 
} from 'react'

import {
  Button,
  Div,
  Footer,
  Sidebar,
  TextLoader 
} from '@driftt/tide-core'
import { tracking } from '@driftt/traffic-control'
import {
  getAllContactEvents,
  getAllContactProperties,
  getAllTags 
} from 'api/contacts'
import {
  getAllPlaybooks,
  getPlaybookEvents 
} from 'api/playbooks'
import { newCreateSegment } from 'api/segments'
import PropertySelection from 'components/ContactFilterBuilder/Views/PropertySelection'

import {
  DefaultFilter,
  DefaultGroup 
} from './constants/SegmentModel'
import {
  ViewHeadings,
  ViewMaps 
} from './Mappings/ViewMaps'

import './style.css'

const ENTITY_TYPES_MAP = {
  'tags': 'CONTACT_TAG',
  'playbookEvents': 'CONTACT_EVENT',
  'events': 'CONTACT_EVENT',
  'contact': 'CONTACT'
}

class ContactFilterBuilder extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentSegment: props.defaultSegment,
      // We check `isLoading` from props to force isLoading = false for the sake of testing
      isLoading: isNil(props.isLoading) ? true : props.isLoading,
      previousView: null,
      hasPopulatedFilter: false,
      hasSegmentName: false,
      isSaving: false
    }
  }

  componentDidMount() {
    this.getFilterableEntities()
  }

  getFilterableEntities = async () => {
    const {
      orgId,
      onReceiveContactProperties,
      onReceiveEventProperties,
      onReceiveTags,
      onReceivePlaybooks 
    } = this.props

    try {
      const { data: contactProperties } = await getAllContactProperties()
      const { data: tags } = await getAllTags()
      const { data: events } = await getAllContactEvents(orgId)
      const { data: playbooks } = await getAllPlaybooks(orgId)

      onReceiveTags(tags)
      onReceivePlaybooks(playbooks)
      onReceiveEventProperties(events.events)
      onReceiveContactProperties(contactProperties)

      this.setState({ isLoading: false })
    } catch(e) {
      console.warn(e)
    }
  }

  changeSegment = (viewToChange, bypassUpdate = false) => {
    const {
      onChangeView,
      onChangeSegment,
      onChangePlaybook,
      onUpdate 
    } = this.props
    const { currentSegment } = this.state

    onChangePlaybook(null)
    onChangeSegment(currentSegment)
    if (!bypassUpdate) {
      onUpdate(currentSegment)
    }

    onChangeView(viewToChange)
  }

  showSegmentNameAndType = () => {
    const { onChangeView } = this.props

    onChangeView('SetName')
  }

  onSelectProperty = (property, entityReference) => {
    const {
      filterIndex,
      groupIndex 
    } = this.props

    this.setState(produce((draft) => {
      draft.currentSegment.segmentGroups[groupIndex].filters[filterIndex].segmentProperty = {
        name: property.name,
        entityType: ENTITY_TYPES_MAP[entityReference],
        subEntityType: entityReference,
      }
    }), () => this.changeSegment('Operator', true))
  }

  onSelectOperator = (operator, value, type) => {
    const {
      filterIndex,
      groupIndex 
    } = this.props

    this.setState(produce((draft) => {
      draft.currentSegment.segmentGroups[groupIndex].filters[filterIndex].operator = operator
      draft.currentSegment.segmentGroups[groupIndex].filters[filterIndex].segmentValue = {
        value: value,
        type: !isNil(type) ? type : 'STRING'
      }
      draft.hasPopulatedFilter = true
    }))
  }

  onAddFilterEntry = ({ groupIndex }) => {
    const { onAddNewFilter } = this.props

    onAddNewFilter(groupIndex)

    this.setState(produce((draft) => {
      draft.currentSegment.segmentGroups[groupIndex].filters = append(DefaultFilter, draft.currentSegment.segmentGroups[groupIndex].filters)
      draft.hasPopulatedFilter = false
    }))
  }

  onDeleteFilterEntry = (groupId, filterId) => {
    this.setState(produce(({ currentSegment }) => {
      currentSegment.segmentGroups[groupId].filters = remove(filterId, 1, currentSegment.segmentGroups[groupId].filters)
    }), () => this.changeSegment('Filter'))
  }

  onEditFilterProperty = ({
    groupIndex,
    filterIndex,
    selectedFilter 
  }) => {
    const { onEditFilterProperty } = this.props

    tracking.trackEvent('Contact Filter Builder - Filter Property Edited', {
      propertyName: selectedFilter.segmentProperty.name,
      propertyType: selectedFilter.segmentProperty.subEntityType,
    })

    onEditFilterProperty({
      groupIndex,
      filterIndex 
    })

    this.setState({ hasPopulatedFilter: false })
  }

  onEditFilterValue = ({
    groupIndex,
    filterIndex,
    selectedFilter 
  }) => {
    const { onEditFilterValue } = this.props

    tracking.trackEvent('Contact Filter Builder - Filter Value Edited', {
      propertyName: selectedFilter.segmentProperty.name,
      propertyType: selectedFilter.segmentProperty.subEntityType,
      operator: selectedFilter.operator
    })

    onEditFilterValue({
      groupIndex,
      filterIndex 
    })
  }

  onAddFilterGroup = () => {
    const { onAddNewGroup } = this.props

    onAddNewGroup()

    this.setState(produce((draft) => {
      draft.currentSegment.segmentGroups = append(DefaultGroup, draft.currentSegment.segmentGroups)
      draft.hasPopulatedFilter = false
    }))
  }

  onDeleteFilterGroup = (groupId) => {
    this.setState(produce(({ currentSegment }) => {
      currentSegment.segmentGroups = remove(groupId, 1, currentSegment.segmentGroups)
    }), () => this.changeSegment('Filter'))
  }

  onChangeGroupLevelJoin = (joinValue, editedGroupIndex) => {
    this.setState(produce(({ currentSegment }) => {
      currentSegment.segmentGroups[editedGroupIndex].matchType = joinValue
    }), () => this.changeSegment('Filter'))
  }

  onChangeTopLevelJoin = (joinValue) => {
    this.setState(produce(({ currentSegment }) => {
      currentSegment.matchType = joinValue
    }), () => this.changeSegment('Filter'))
  }

  onApplyFilter = () => {
    const { currentSegment } = this.state
    const {
      onUpdate,
      onApplyFilter,
      onChangeSegment 
    } = this.props

    onChangeSegment(currentSegment)
    onUpdate(currentSegment)

    onApplyFilter()
  }

  onChangeSegmentType = (type) => {
    this.setState(produce((draft) => {
      draft.currentSegment.type = type
    }))
  }

  onChangeSegmentName = (value) => {
    this.setState(produce((draft) => {
      draft.currentSegment.name = value
      draft.hasSegmentName = true
    }), () => this.changeSegment('SetName', true))
  }

  handleSaveSegment = () => {
    tracking.trackEvent('Contact Filter Builder - Segment Saved')

    this.setState({ isSaving: true }, this.onSaveSegment)
  }

  onSaveSegment = async () => {
    const { currentSegment } = this.state
    const {
      onSave,
      showTransientNotification,
      close,
      onSaveSegment 
    } = this.props

    try {
      const { data } = await newCreateSegment(currentSegment)

      onSave(data)
      onSaveSegment()

      showTransientNotification({
        type: 'success',
        message: 'Segment Successfully Created' 
      })

      close()
    } catch(e) {
      console.warn()
    }
  }

  onSelectPlaybook = async (playbook) => {
    const {
      onChangePlaybook,
      onReceivePlaybookEvents,
      onChangeView 
    } = this.props

    onChangePlaybook(playbook)

    try {
      const playbookEvents = await getPlaybookEvents(playbook.id)

      onReceivePlaybookEvents(playbookEvents)

      onChangeView('PBEvents')
    } catch(e) {
      console.warn(e)
    }
  }

  returnToPreviousScreen = (bypassClear = false) => {
    const {
      onChangePlaybook,
      onReturnToPreviousView 
    } = this.props

    if (!bypassClear) {
      this.setState({ hasPopulatedFilter: false })

      onChangePlaybook(null)
    }

    onReturnToPreviousView()
  }

  handleCloseFilterBuilder = () => {
    const {
      onCloseFilterBuilder,
      close 
    } = this.props

    close()
    onCloseFilterBuilder()
  }

  getCurrentFilter = () => {
    const {
      filterIndex,
      groupIndex 
    } = this.props
    const { currentSegment } = this.state

    if (isNil(filterIndex) || isNil(groupIndex)) {
      return null
    }

    const result = currentSegment.segmentGroups[groupIndex].filters[filterIndex]
    return result
  }

  getSelectedProp = () => {
    const {
      contactProperties,
      eventProperties,
      playbookEventProperties,
      tagProperties 
    } = this.props

    const currentFilter = this.getCurrentFilter()

    if (!currentFilter) {
      return null
    }

    const currentSegmentProperty = currentFilter.segmentProperty

    if (!currentSegmentProperty.subEntityType) {
      return null
    }

    let propertyDefinitions

    switch (currentSegmentProperty.subEntityType) {
    case 'contact':
      propertyDefinitions = contactProperties
      break
    case 'tags':
      propertyDefinitions = tagProperties
      break
    case 'events':
      propertyDefinitions = eventProperties
      break
    case 'playbookEvents':
      propertyDefinitions = playbookEventProperties
      break
    default:
      throw new Error(`Expected current filter's subEntityType to be one of 'contact', 'tags', 'events', 'playbookEvents'. Got '${currentSegmentProperty.subEntityType}'`)
    }

    const selectedProp = propertyDefinitions.find(propDef => propDef.name === currentSegmentProperty.name)

    if (currentSegmentProperty.subEntityType === 'events') {
      return {
        ...selectedProp,
        type: 'EVENT'
      }
    }

    return selectedProp
  }

  renderFooter = () => {
    const { view } = this.props
    const {
      hasPopulatedFilter,
      hasSegmentName,
      isSaving 
    } = this.state

    if (view !== 'Filter' && view !== 'SetName') {
      return (
        <Footer className="filter-builder--buttons">
          <Button className="filter-builder--apply" size="medium"
            type="primary" disabled={!hasPopulatedFilter}
            onClick={this.onApplyFilter}>Apply filter
          </Button>
        </Footer>
      )
    } else if (view === 'SetName') {
      return (
        <Footer className="filter-builder--buttons">
          <Button className="filter-builder--apply" size="medium"
            type="primary" disabled={!hasSegmentName || isSaving}
            loading={isSaving} onClick={this.handleSaveSegment}>Create segment
          </Button>
        </Footer>
      )
    } else {
      return (
        <Footer className="filter-builder--buttons">
          <Button className="filter-builder--apply" size="medium"
            type="tertiary" onClick={this.showSegmentNameAndType}>Save as segment
          </Button>
          <Button type="tertiary" onClick={this.handleCloseFilterBuilder}
            size="medium">Clear filters
          </Button>
        </Footer>
      )
    }
  }

  render() {
    const {
      open,
      view,
      close 
    } = this.props
    const {
      isLoading,
      currentSegment,
      previousView 
    } = this.state

    const heading = ViewHeadings[view]

    const RenderView = ViewMaps[view]

    const shouldShowPropertySelection = ['Selection', 'Properties', 'PBSelection', 'Tags', 'Events'].includes(view)

    return (
      <Fragment>
        <Sidebar
          isOpen={open}
          showClose={true}
          close={close}
          hideOverlay={true}
          title={heading}
          className="contact-filter-builder--sidebar"
        >
          {isLoading && <TextLoader text="Synchronizing bits" className="filter-builder--loading"/>}
          {!isLoading &&
            <Div className="filter-builder--content">
              {shouldShowPropertySelection && <PropertySelection previousScreen={previousView} />}
              {RenderView && <RenderView
                onSelectProp={this.onSelectProperty}
                onSelectOp={this.onSelectOperator}
                onChangeGroupJoin={this.onChangeGroupLevelJoin}
                onChangeTopJoin={this.onChangeTopLevelJoin}
                onAddFilter={this.onAddFilterEntry}
                onDeleteFilter={this.onDeleteFilterEntry}
                onEditFilterProperty={this.onEditFilterProperty}
                onEditFilterValue={this.onEditFilterValue}
                onAddGroup={this.onAddFilterGroup}
                onDeleteGroup={this.onDeleteFilterGroup}
                onChangeSegmentName={this.onChangeSegmentName}
                onChangeSegmentType={this.onChangeSegmentType}
                currentSegment={currentSegment}
                currentFilter={this.getCurrentFilter()}
                selectedProp={this.getSelectedProp()}
                onPlaybookChoose={this.onSelectPlaybook}
                onBackNavigation={this.returnToPreviousScreen}
              />}
            </Div>
          }
          {this.renderFooter()}
        </Sidebar>
      </Fragment>
    )
  }
}

export default ContactFilterBuilder
